import React from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { formatDistanceToNowStrict } from "date-fns";
import { ChevronsDown, ChevronsUp, EyeOff, LayoutGrid, Maximize2, Minus, Pin, Plus, StickyNote as StickyIcon, Trash2 } from "lucide-react";
import { useAddStickyNote, useDeleteStickyNote, useMinimizeAllStickyNotes, useStickyNotes, useUpdateStickyNote, type NoteColor, type NotePatch, type StickyNote } from "../../api/stickyNotes";
import { useToast } from "../../context/ToastContext";
import { extractApiError } from "../../lib/apiClient";

/**
 * Request 1223 — Sticky Notes. "Sticky Notes" in the menu deals your notes onto whatever screen you're on — they fly out of the
 * menu item, tumble and land with a little bounce. Type anything; drag a note by its top (it tilts with the speed you move it and
 * lands with a squash), resize it from its curled corner, recolour it (the new colour washes across the paper from the dot you
 * clicked), pin it so it can't move, or delete it (it crumples into a ball and drops away). "Hide" sweeps them all back into the
 * menu item. Notes are private, saved to the account, and float above every page without blocking it.
 */

// ------------------------------------------------------------------------------------------------ paper colours (fixed — paper looks like paper in light and dark)
export const NOTE_COLORS: Record<NoteColor, { paper: string; edge: string; ink: string; dot: string; label: string }> = {
  lemon: { paper: "#fff7b0", edge: "#fde68a", ink: "#4a3f0b", dot: "#facc15", label: "Lemon" },
  rose: { paper: "#ffdbe5", edge: "#fbb6c9", ink: "#5b1a2e", dot: "#f472b6", label: "Rose" },
  mint: { paper: "#d2f8e4", edge: "#a7ebc8", ink: "#0f3d2a", dot: "#34d399", label: "Mint" },
  sky: { paper: "#d6ebff", edge: "#acd5fb", ink: "#0f2e4d", dot: "#38bdf8", label: "Sky" },
  lilac: { paper: "#e9e0ff", edge: "#cdbbfb", ink: "#2e1f5c", dot: "#a78bfa", label: "Lilac" },
  peach: { paper: "#ffe3c7", edge: "#fdc89c", ink: "#5a2d0c", dot: "#fb923c", label: "Peach" },
};
const COLOR_KEYS = Object.keys(NOTE_COLORS) as NoteColor[];

/** a steady small resting tilt per note, so a pile of notes looks hand-placed */
const tiltOf = (id: string) => ((([...id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7) % 9) - 4) * 0.5);

// ------------------------------------------------------------------------------------------------ context: the menu item opens / closes the layer
type Origin = { x: number; y: number };
const Ctx = React.createContext<{ open: boolean; count: number; toggle: (from?: DOMRect) => void }>({ open: false, count: 0, toggle: () => {} });
export const useStickyNotesLayer = () => React.useContext(Ctx);

type Phase = "closed" | "opening" | "open" | "closing";

export function StickyNotesProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = React.useState<Phase>("closed");
  const origin = React.useRef<Origin>({ x: 120, y: 160 });
  const { data } = useStickyNotes();
  const count = data?.length ?? 0;
  const timer = React.useRef<number>();

  const show = React.useCallback((from?: DOMRect) => {
    if (from) origin.current = { x: from.left + from.width / 2, y: from.top + from.height / 2 };
    window.clearTimeout(timer.current);
    setPhase("opening");
    timer.current = window.setTimeout(() => setPhase("open"), 900);
  }, []);
  const hide = React.useCallback(() => {
    window.clearTimeout(timer.current);
    setPhase("closing");
    timer.current = window.setTimeout(() => setPhase("closed"), 700);
  }, []);
  const toggle = React.useCallback((from?: DOMRect) => (phase === "closed" || phase === "closing" ? show(from) : hide()), [phase, show, hide]);

  return (
    <Ctx.Provider value={{ open: phase === "opening" || phase === "open", count, toggle }}>
      {children}
      {phase !== "closed" && <NotesLayer phase={phase} origin={origin.current} onHide={hide} />}
    </Ctx.Provider>
  );
}

// ------------------------------------------------------------------------------------------------ the layer over the screen
function NotesLayer({ phase, origin, onHide }: { phase: Phase; origin: Origin; onHide: () => void }) {
  const { push } = useToast();
  const { data, isLoading } = useStickyNotes();
  const add = useAddStickyNote();
  const update = useUpdateStickyNote();
  const del = useDeleteStickyNote();
  const minAll = useMinimizeAllStickyNotes();
  const notes = data ?? [];
  const anyOpen = notes.some((n) => !n.minimized);
  const [newColor, setNewColor] = React.useState<NoteColor>("lemon");
  const [fresh, setFresh] = React.useState<Record<string, Origin>>({}); // notes just created → peel off the pad from here
  const [tidying, setTidying] = React.useState(false);
  const plusRef = React.useRef<HTMLButtonElement>(null);
  const autoMade = React.useRef(false);
  const topZ = notes.reduce((m, n) => Math.max(m, n.z), 0);

  const save = React.useCallback(
    (id: string, patch: NotePatch) =>
      update.mutate({ id, ...patch }, { onError: (err) => push({ variant: "error", title: "Couldn't save the note", description: extractApiError(err).message }) }),
    [update, push]
  );

  const create = React.useCallback(
    async (color: NoteColor) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const k = notes.length % 6;
      const width = Math.min(280, vw - 32);
      const x = Math.max(16, Math.min(vw - width - 16, vw / 2 - width / 2 + (k - 2.5) * 36));
      const y = Math.max(72, Math.min(vh - 300, vh / 2 - 170 + (k - 2.5) * 22));
      try {
        const n = await add.mutateAsync({ color, x, y, width, height: 250, z: topZ + 1 });
        const r = plusRef.current?.getBoundingClientRect();
        if (r) setFresh((f) => ({ ...f, [n.id]: { x: r.left + r.width / 2, y: r.top + r.height / 2 } }));
        // the peel animation holds its last frame — drop it once it has played so the note can be dragged
        window.setTimeout(() => setFresh(({ [n.id]: _, ...rest }) => rest), 800);
        window.setTimeout(() => document.getElementById(`sn-text-${n.id}`)?.focus(), 650);
      } catch (err) {
        push({ variant: "error", title: "Couldn't add a note", description: extractApiError(err).message });
      }
    },
    [add, notes.length, topZ, push]
  );

  // the first time the layer opens with no notes, start one
  React.useEffect(() => {
    if (!isLoading && data && data.length === 0 && phase === "opening" && !autoMade.current) {
      autoMade.current = true;
      void create("lemon");
    }
  }, [isLoading, data, phase, create]);

  // Esc hides (unless you're typing — then it just leaves the note)
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const el = document.activeElement as HTMLElement | null;
      if (el?.dataset.stickyText) return el.blur();
      onHide();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onHide]);

  // keep notes reachable if the window got smaller since they were placed
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  function tidy() {
    setTidying(true);
    const w = 270;
    const gap = 18;
    const cols = Math.max(1, Math.floor((vw - 80) / (w + gap)));
    [...notes]
      .sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt))
      .forEach((n, i) => {
        const x = vw - 40 - (cols - (i % cols)) * (w + gap) + gap;
        const y = 76 + Math.floor(i / cols) * (230 + gap);
        save(n.id, { x, y, width: w, height: 230 });
      });
    window.setTimeout(() => setTidying(false), 700);
  }

  return createPortal(
    <div className={clsx("sn-layer pointer-events-none fixed inset-0 z-[70]", phase === "closing" && "sn-closing")} aria-label="Sticky notes">
      <style>{SN_CSS}</style>
      {notes.map((n, i) => (
        <Note
          key={n.id}
          note={{ ...n, x: Math.min(Math.max(n.x, -n.width + 90), vw - 90), y: Math.min(Math.max(n.y, 0), vh - 48) }}
          index={i}
          phase={phase}
          origin={origin}
          fresh={fresh[n.id]}
          tidying={tidying}
          topZ={topZ}
          onSave={save}
          onDelete={() => del.mutate(n.id, { onError: (err) => push({ variant: "error", title: "Couldn't delete the note", description: extractApiError(err).message }) })}
        />
      ))}

      {/* the dock */}
      <div className={clsx("sn-dock pointer-events-auto fixed bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-white/10 bg-slate-900/90 p-1.5 pl-3 text-white shadow-2xl backdrop-blur-md", phase === "closing" ? "sn-dock-out" : "sn-dock-in")}>
        <StickyIcon className="h-4 w-4 text-amber-300" />
        <span className="mr-1 text-xs font-semibold tabular-nums text-slate-300">
          {notes.length} note{notes.length === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-1 rounded-full bg-white/5 px-1.5 py-1">
          {COLOR_KEYS.map((c) => (
            <button
              key={c}
              type="button"
              title={`New notes in ${NOTE_COLORS[c].label}`}
              onClick={() => setNewColor(c)}
              className={clsx("h-4 w-4 rounded-full ring-2 transition-transform hover:scale-125", newColor === c ? "scale-110 ring-white" : "ring-transparent")}
              style={{ background: NOTE_COLORS[c].dot }}
            />
          ))}
        </div>
        <button
          ref={plusRef}
          type="button"
          onClick={() => create(newColor)}
          disabled={add.isPending}
          className="sn-plus inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 px-3 py-1.5 text-xs font-bold text-slate-900 shadow-md transition hover:brightness-110 disabled:opacity-60"
        >
          <Plus className="h-3.5 w-3.5" /> New note
        </button>
        <button
          type="button"
          onClick={() => minAll.mutate(anyOpen, { onError: (err) => push({ variant: "error", title: "Couldn't update the notes", description: extractApiError(err).message }) })}
          disabled={!notes.length}
          title={anyOpen ? "Minimise all" : "Restore all"}
          className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white disabled:opacity-40"
        >
          {anyOpen ? <ChevronsDown className="h-4 w-4" /> : <ChevronsUp className="h-4 w-4" />}
        </button>
        <button type="button" onClick={tidy} disabled={!notes.length} title="Line them up neatly" className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white disabled:opacity-40">
          <LayoutGrid className="h-4 w-4" />
        </button>
        <button type="button" onClick={onHide} title="Hide notes (Esc)" className="rounded-full p-2 text-slate-300 transition hover:bg-white/10 hover:text-white">
          <EyeOff className="h-4 w-4" />
        </button>
      </div>
    </div>,
    document.body
  );
}

// ------------------------------------------------------------------------------------------------ one note
function Note({
  note,
  index,
  phase,
  origin,
  fresh,
  tidying,
  topZ,
  onSave,
  onDelete,
}: {
  note: StickyNote;
  index: number;
  phase: Phase;
  origin: Origin;
  fresh?: Origin;
  tidying: boolean;
  topZ: number;
  onSave: (id: string, patch: NotePatch) => void;
  onDelete: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [text, setText] = React.useState(note.text);
  const [saving, setSaving] = React.useState<"idle" | "typing" | "saved">("idle");
  const [mode, setMode] = React.useState<"rest" | "drag" | "resize" | "landed" | "crumple">("rest");
  const [wash, setWash] = React.useState<{ color: NoteColor; x: number; y: number; k: number } | null>(null);
  const [pinDrop, setPinDrop] = React.useState(false);
  const pos = React.useRef({ x: note.x, y: note.y, w: note.width, h: note.height });
  const drag = React.useRef<{ dx: number; dy: number; lastX: number; tilt: number; raf: number; sx: number; sy: number } | null>(null);
  const typingTimer = React.useRef<number>();
  const c = NOTE_COLORS[note.color] ?? NOTE_COLORS.lemon;
  const tilt = tiltOf(note.id);
  // request 1224: minimised — rolled up into a small tab showing the first line
  const min = note.minimized;
  const firstLine = (text.split("\n").find((l) => l.trim()) ?? "").trim() || "Empty note";
  const toggleMin = () => {
    front();
    onSave(note.id, { minimized: !min });
  };

  // follow outside changes (tidy up, other device) when not being handled
  React.useEffect(() => {
    if (mode === "drag" || mode === "resize") return;
    pos.current = { x: note.x, y: note.y, w: note.width, h: note.height };
  }, [note.x, note.y, note.width, note.height, mode]);
  React.useEffect(() => {
    if (document.activeElement?.id !== `sn-text-${note.id}`) setText(note.text);
  }, [note.text, note.id]);

  const front = () => {
    if (note.z < topZ) onSave(note.id, { z: topZ + 1 });
  };

  // --- typing (saved a moment after you stop)
  const onType = (v: string) => {
    setText(v);
    setSaving("typing");
    window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => {
      onSave(note.id, { text: v });
      setSaving("saved");
    }, 650);
  };
  React.useEffect(() => () => window.clearTimeout(typingTimer.current), []);

  // --- dragging by the top strip: tilts with your speed, lands with a squash
  const onDragStart = (e: React.PointerEvent) => {
    if (note.pinned || (e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    window.getSelection()?.removeAllRanges();
    front();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { dx: e.clientX - pos.current.x, dy: e.clientY - pos.current.y, lastX: e.clientX, tilt: 0, raf: 0, sx: pos.current.x, sy: pos.current.y };
    setMode("drag");
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d || mode !== "drag") return;
    const vx = e.clientX - d.lastX;
    d.lastX = e.clientX;
    d.tilt = Math.max(-16, Math.min(16, d.tilt * 0.75 + vx * 0.9));
    pos.current.x = Math.min(Math.max(e.clientX - d.dx, -pos.current.w + 90), window.innerWidth - 90);
    pos.current.y = Math.min(Math.max(e.clientY - d.dy, 0), window.innerHeight - 48);
    cancelAnimationFrame(d.raf);
    d.raf = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.style.left = `${pos.current.x}px`;
      el.style.top = `${pos.current.y}px`;
      el.style.setProperty("--drag-tilt", `${d.tilt}deg`);
    });
  };
  const onDragEnd = () => {
    const d = drag.current;
    if (!d) return;
    cancelAnimationFrame(d.raf);
    drag.current = null;
    ref.current?.style.setProperty("--drag-tilt", "0deg");
    setMode("landed");
    window.setTimeout(() => setMode((m) => (m === "landed" ? "rest" : m)), 520);
    if (Math.abs(pos.current.x - d.sx) > 1 || Math.abs(pos.current.y - d.sy) > 1) onSave(note.id, { x: pos.current.x, y: pos.current.y });
  };

  // --- resizing from the curled corner
  // request 1224: resize from the right, bottom or left edge, or either bottom corner (the left side moves the note too)
  type Dir = "r" | "b" | "l" | "br" | "bl";
  const rs = React.useRef<{ sx: number; sy: number; w: number; h: number; x: number; dir: Dir } | null>(null);
  const onResizeStart = (dir: Dir) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.getSelection()?.removeAllRanges();
    front();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    rs.current = { sx: e.clientX, sy: e.clientY, w: pos.current.w, h: pos.current.h, x: pos.current.x, dir };
    setMode("resize");
  };
  const onResizeMove = (e: React.PointerEvent) => {
    const r = rs.current;
    if (!r) return;
    const dx = e.clientX - r.sx;
    const dy = e.clientY - r.sy;
    if (r.dir.includes("r")) pos.current.w = Math.min(900, Math.max(180, r.w + dx));
    if (r.dir.includes("l")) {
      const w = Math.min(900, Math.max(180, r.w - dx));
      pos.current.x = r.x + (r.w - w);
      pos.current.w = w;
    }
    if (r.dir.includes("b")) pos.current.h = Math.min(900, Math.max(140, r.h + dy));
    const el = ref.current;
    if (el) {
      el.style.width = `${pos.current.w}px`;
      el.style.height = `${pos.current.h}px`;
      el.style.left = `${pos.current.x}px`;
    }
  };
  const onResizeEnd = () => {
    const r = rs.current;
    if (!r) return;
    rs.current = null;
    setMode("rest");
    onSave(note.id, { width: pos.current.w, height: pos.current.h, ...(r.dir.includes("l") ? { x: pos.current.x } : {}) });
  };
  const grip = (dir: Dir) => ({ onPointerDown: onResizeStart(dir), onPointerMove: onResizeMove, onPointerUp: onResizeEnd, onPointerCancel: onResizeEnd });

  // --- recolour: the new colour washes across the paper from the dot you clicked
  const recolour = (to: NoteColor, e: React.MouseEvent) => {
    if (to === note.color) return;
    const box = ref.current?.getBoundingClientRect();
    setWash({ color: to, x: box ? e.clientX - box.left : 0, y: box ? e.clientY - box.top : 0, k: Date.now() });
    window.setTimeout(() => {
      onSave(note.id, { color: to });
      setWash(null);
    }, 520);
  };

  const togglePin = () => {
    if (!note.pinned) {
      setPinDrop(true);
      window.setTimeout(() => setPinDrop(false), 600);
    }
    onSave(note.id, { pinned: !note.pinned });
  };

  const remove = () => {
    setMode("crumple");
    window.setTimeout(onDelete, 760);
  };

  // where the note travels from / to when the layer opens or closes
  const from = fresh ?? origin;
  const cx = note.x + note.width / 2;
  const cy = note.y + note.height / 2;
  const anim =
    mode === "crumple"
      ? "sn-crumple"
      : phase === "closing"
        ? "sn-return"
        : fresh
          ? "sn-peel"
          : phase === "opening"
            ? "sn-deal"
            : mode === "landed"
              ? "sn-land"
              : "";

  return (
    <div
      ref={ref}
      onPointerDown={front}
      className={clsx("sn-note pointer-events-auto absolute flex flex-col rounded-[3px]", anim, mode === "drag" && "sn-dragging", tidying && "sn-tidy", note.pinned && "sn-pinned", min && "sn-min", mode === "resize" && "sn-resizing")}
      style={
        {
          left: note.x,
          top: note.y,
          width: min ? Math.min(note.width, 230) : note.width,
          height: min ? 40 : note.height,
          zIndex: 10 + note.z,
          background: `linear-gradient(180deg, ${c.edge} 0, ${c.paper} 30px, ${c.paper} 100%)`,
          color: c.ink,
          "--tilt": `${tilt}deg`,
          "--fx": `${from.x - cx}px`,
          "--fy": `${from.y - cy}px`,
          "--delay": `${(fresh ? 0 : index) * 70}ms`,
          "--ink": c.ink,
        } as React.CSSProperties
      }
    >
      {/* tape */}
      {!note.pinned && <span aria-hidden className="sn-tape" />}
      {note.pinned && (
        <span aria-hidden className={clsx("sn-pin", pinDrop && "sn-pin-drop")}>
          <span />
        </span>
      )}
      {/* recolour wash */}
      {wash && <span key={wash.k} aria-hidden className="sn-wash" style={{ background: NOTE_COLORS[wash.color].paper, ["--wx" as any]: `${wash.x}px`, ["--wy" as any]: `${wash.y}px` }} />}

      {/* top strip: drag here */}
      <div
        className={clsx("sn-head relative z-[1] flex h-8 shrink-0 select-none items-center gap-1 px-2", note.pinned ? "cursor-default" : "cursor-grab active:cursor-grabbing")}
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
        title={note.pinned ? "Pinned — unpin to move it" : "Drag to move"}
      >
        {min ? (
          <button type="button" onClick={toggleMin} className="sn-min-title min-w-0 flex-1 truncate text-left text-[12.5px] font-semibold" title="Restore">
            {firstLine}
          </button>
        ) : (
        <div className="sn-tools flex items-center gap-1">
          {COLOR_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              title={NOTE_COLORS[k].label}
              onClick={(e) => recolour(k, e)}
              className={clsx("h-3.5 w-3.5 rounded-full border border-black/10 transition-transform hover:scale-125", note.color === k && "ring-2 ring-black/25 ring-offset-1")}
              style={{ background: NOTE_COLORS[k].dot }}
            />
          ))}
        </div>
        )}
        {!min && <span className="flex-1" />}
        <button type="button" onClick={toggleMin} title={min ? "Restore" : "Minimise"} className={clsx("sn-tools rounded p-1 transition hover:bg-black/10", min && "!opacity-100")}>
          {min ? <Maximize2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
        </button>
        <button type="button" onClick={togglePin} title={note.pinned ? "Unpin" : "Pin it in place"} className={clsx("sn-tools rounded p-1 transition hover:bg-black/10", note.pinned && "!opacity-100")}>
          <Pin className={clsx("h-3.5 w-3.5", note.pinned && "fill-current")} />
        </button>
        <button type="button" onClick={remove} title="Delete note" className="sn-tools rounded p-1 transition hover:bg-black/10 hover:text-red-700">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {!min && (
      <>
      <textarea
        id={`sn-text-${note.id}`}
        data-sticky-text="1"
        value={text}
        onChange={(e) => onType(e.target.value)}
        onBlur={() => {
          if (text !== note.text) {
            window.clearTimeout(typingTimer.current);
            onSave(note.id, { text });
            setSaving("saved");
          }
        }}
        placeholder="Type anything…"
        spellCheck
        className="sn-text relative z-[1] min-h-0 flex-1 resize-none border-0 bg-transparent px-3 pb-1 pt-0.5 text-[13.5px] leading-[21px] outline-none placeholder:opacity-50"
        style={{ color: c.ink }}
      />

      <div className="relative z-[1] flex h-6 shrink-0 items-center gap-2 px-3 text-[10px] opacity-60">
        <span>{saving === "typing" ? "Saving…" : `Edited ${formatDistanceToNowStrict(new Date(note.updatedAt), { addSuffix: true })}`}</span>
        <span className="flex-1" />
        <span className="tabular-nums">{text.length ? `${text.length} chars` : ""}</span>
      </div>

      {/* resize: edges and bottom corners; the curled corner shows a grip */}
      <span aria-hidden className="sn-edge sn-edge-r" {...grip("r")} />
      <span aria-hidden className="sn-edge sn-edge-l" {...grip("l")} />
      <span aria-hidden className="sn-edge sn-edge-b" {...grip("b")} />
      <span aria-hidden className="sn-edge sn-edge-bl" {...grip("bl")} />
      <span aria-hidden className="sn-curl" style={{ ["--curl" as any]: c.edge, ["--ink" as any]: c.ink }} title="Drag to resize" {...grip("br")}>
        <svg viewBox="0 0 10 10" className="sn-grip">
          <path d="M9 3 3 9M9 6 6 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
        </svg>
      </span>
      </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ the menu item
export function StickyNotesMenuItem({ onNavigate }: { onNavigate?: () => void }) {
  const { open, count, toggle } = useStickyNotesLayer();
  return (
    <button
      type="button"
      onClick={(e) => {
        toggle(e.currentTarget.getBoundingClientRect());
        if (!open) onNavigate?.();
      }}
      className={clsx(
        "nav-hover group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
        open ? "bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 shadow-sm" : "text-slate-300 hover:bg-white/5 hover:text-white"
      )}
    >
      <span className="relative h-4 w-4 shrink-0">
        <StickyIcon className={clsx("h-4 w-4 transition-transform duration-300", open ? "-rotate-12 scale-110" : "group-hover:-rotate-12")} />
        {!open && <span className="sn-menu-peek" aria-hidden />}
        <style>{SN_MENU_CSS}</style>
      </span>
      <span className="flex-1 text-left">Sticky Notes</span>
      {count > 0 && <span className={clsx("flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold", open ? "bg-slate-900/15 text-slate-900" : "bg-amber-400/20 text-amber-300")}>{count}</span>}
    </button>
  );
}

// ------------------------------------------------------------------------------------------------ styles
const SN_CSS = `
.sn-note{
  transform: rotate(calc(var(--tilt) + var(--drag-tilt, 0deg)));
  box-shadow: 0 1px 1px rgba(0,0,0,.08), 0 6px 14px -4px rgba(0,0,0,.22), 0 18px 30px -18px rgba(0,0,0,.35);
  transition: transform .35s cubic-bezier(.34,1.56,.64,1), box-shadow .3s ease, width .2s, height .2s;
  font-family: "Inter", ui-sans-serif, system-ui, sans-serif;
}
.sn-note:hover{ box-shadow: 0 1px 1px rgba(0,0,0,.08), 0 10px 22px -6px rgba(0,0,0,.28), 0 26px 40px -20px rgba(0,0,0,.4); }
.sn-note.sn-dragging{
  transform: rotate(var(--drag-tilt, 0deg)) scale(1.05);
  box-shadow: 0 2px 2px rgba(0,0,0,.06), 0 24px 40px -10px rgba(0,0,0,.45), 0 40px 60px -30px rgba(0,0,0,.5);
  transition: box-shadow .2s ease, transform .08s linear;
}
.sn-note.sn-dragging, .sn-note.sn-dragging *{ user-select:none; }
.sn-note.sn-tidy{ transition: left .6s cubic-bezier(.34,1.3,.64,1), top .6s cubic-bezier(.34,1.3,.64,1), width .6s, height .6s, transform .35s; }
.sn-tools{ opacity: 0; transition: opacity .2s; }
/* request 1224: minimised — the paper rolls up into a tab; restoring unrolls it */
.sn-note{ transition: transform .35s cubic-bezier(.34,1.56,.64,1), box-shadow .3s ease, width .45s cubic-bezier(.34,1.3,.64,1), height .45s cubic-bezier(.34,1.3,.64,1); }
.sn-note.sn-resizing{ transition: none; }
.sn-note.sn-min{ overflow:hidden; border-radius:8px; }
.sn-note.sn-min .sn-head{ height:40px; }
.sn-note.sn-min .sn-tape{ display:none; }
.sn-note.sn-min::after{ content:""; position:absolute; left:0; right:0; bottom:0; height:6px; background: linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,.12)); border-radius:0 0 8px 8px; pointer-events:none; }
.sn-note:not(.sn-min) .sn-text{ animation: sn-unroll .45s cubic-bezier(.34,1.3,.64,1) both; transform-origin: 50% 0; }
@keyframes sn-unroll{ from{ opacity:0; transform: scaleY(.6) } to{ opacity:1; transform: scaleY(1) } }
.sn-note:hover .sn-tools, .sn-note:focus-within .sn-tools{ opacity: 1; }
.sn-text::-webkit-scrollbar-thumb{ background-color: rgba(0,0,0,.15); }

/* tape across the top */
.sn-tape{
  position:absolute; left:50%; top:-10px; width:78px; height:22px; z-index:2; pointer-events:none;
  transform: translateX(-50%) rotate(-3deg);
  background: linear-gradient(90deg, rgba(255,255,255,.55), rgba(255,255,255,.75) 50%, rgba(255,255,255,.55));
  box-shadow: 0 1px 2px rgba(0,0,0,.12);
  clip-path: polygon(3% 0, 97% 4%, 100% 50%, 96% 100%, 4% 96%, 0 52%);
  backdrop-filter: blur(1px);
}
/* push-pin when pinned */
.sn-pin{ position:absolute; left:50%; top:-9px; z-index:3; width:18px; height:18px; transform:translateX(-50%); pointer-events:none; }
.sn-pin::before{ content:""; position:absolute; inset:0; border-radius:50%; background: radial-gradient(circle at 35% 30%, #fca5a5, #dc2626 55%, #7f1d1d); box-shadow: 0 3px 5px rgba(0,0,0,.35); }
.sn-pin > span{ position:absolute; left:8px; top:14px; width:2px; height:7px; background:#64748b; border-radius:1px; }
.sn-pin-drop{ animation: sn-pin .6s cubic-bezier(.34,1.56,.64,1) both; }
@keyframes sn-pin{ 0%{ transform: translate(-50%,-26px) scale(1.6); opacity:0 } 60%{ transform: translate(-50%,2px) scale(.9); opacity:1 } 100%{ transform: translate(-50%,0) scale(1) } }

/* curled bottom-right corner — also the resize handle */
.sn-curl{
  position:absolute; right:0; bottom:0; width:22px; height:22px; z-index:2; cursor: nwse-resize; touch-action:none;
  background: linear-gradient(135deg, transparent 50%, var(--curl) 50%, rgba(0,0,0,.08) 100%);
  box-shadow: -2px -2px 3px -2px rgba(0,0,0,.2);
  border-top-left-radius: 6px;
  transition: width .25s, height .25s;
}
.sn-note:hover .sn-curl{ width:30px; height:30px; }
.sn-grip{ position:absolute; right:3px; bottom:3px; width:11px; height:11px; color: var(--ink); opacity:0; transition: opacity .2s; }
.sn-note:hover .sn-grip{ opacity:.55; }
/* request 1224: invisible grab strips along the edges */
.sn-edge{ position:absolute; z-index:2; touch-action:none; }
.sn-edge-r{ top:32px; right:-4px; bottom:22px; width:9px; cursor: ew-resize; }
.sn-edge-l{ top:32px; left:-4px; bottom:22px; width:9px; cursor: ew-resize; }
.sn-edge-b{ left:22px; right:30px; bottom:-4px; height:9px; cursor: ns-resize; }
.sn-edge-bl{ left:-4px; bottom:-4px; width:22px; height:22px; cursor: nesw-resize; }
.sn-edge:hover{ background: linear-gradient(var(--ink), var(--ink)) center / 2px 40% no-repeat; opacity:.25; }
.sn-edge-b:hover{ background-size: 40% 2px; }
.sn-edge-bl:hover{ background:none; }

/* the new colour washes across from the dot */
.sn-wash{ position:absolute; inset:0; z-index:0; border-radius:3px; animation: sn-wash .52s ease-out both; }
@keyframes sn-wash{ from{ clip-path: circle(0 at var(--wx) var(--wy)) } to{ clip-path: circle(150% at var(--wx) var(--wy)) } }

/* dealt out of the menu item: tumbles in, overshoots, settles */
.sn-deal{ animation: sn-deal .8s cubic-bezier(.22,1.2,.36,1) var(--delay) both; }
@keyframes sn-deal{
  0%{ transform: translate(var(--fx), var(--fy)) scale(.12) rotate(-38deg); opacity:0 }
  25%{ opacity:1 }
  70%{ transform: translate(0,0) scale(1.04) rotate(calc(var(--tilt) + 4deg)) }
  100%{ transform: translate(0,0) scale(1) rotate(var(--tilt)) }
}
/* swept back into the menu item */
.sn-return{ animation: sn-return .6s cubic-bezier(.55,0,.75,.2) calc(var(--delay) / 2) both; }
@keyframes sn-return{
  0%{ transform: rotate(var(--tilt)) }
  25%{ transform: translate(0,-10px) scale(1.04) rotate(calc(var(--tilt) - 4deg)) }
  100%{ transform: translate(var(--fx), var(--fy)) scale(.08) rotate(40deg); opacity:0 }
}
/* a new note peels off the pad: unfolds from the top while it flies out of the "New note" button */
.sn-peel{ animation: sn-peel .75s cubic-bezier(.22,1.25,.36,1) both; transform-origin: 50% 0; }
@keyframes sn-peel{
  0%{ transform: translate(var(--fx), var(--fy)) scale(.2) rotate(12deg) rotateX(80deg); clip-path: inset(0 0 100% 0); opacity:.4 }
  40%{ clip-path: inset(0 0 30% 0); opacity:1 }
  75%{ transform: translate(0,6px) scale(1.03) rotate(calc(var(--tilt) - 3deg)) rotateX(0deg); clip-path: inset(0 0 0 0) }
  100%{ transform: translate(0,0) scale(1) rotate(var(--tilt)); clip-path: inset(0 0 0 0) }
}
/* lands after a drag: a quick squash */
.sn-land{ animation: sn-land .5s cubic-bezier(.34,1.56,.64,1) both; }
@keyframes sn-land{
  0%{ transform: rotate(var(--tilt)) scale(1.05) }
  35%{ transform: rotate(var(--tilt)) scale(1.03,.95) }
  65%{ transform: rotate(var(--tilt)) scale(.98,1.02) }
  100%{ transform: rotate(var(--tilt)) scale(1) }
}
/* deleted: crumples into a ball and drops away */
.sn-crumple{ animation: sn-crumple .76s cubic-bezier(.55,0,.4,1) both; pointer-events:none; }
@keyframes sn-crumple{
  0%{ transform: rotate(var(--tilt)) scale(1); border-radius:3px; filter:none }
  30%{ transform: rotate(18deg) scale(.82,.7); border-radius:28%; filter: contrast(1.1) }
  55%{ transform: rotate(160deg) scale(.32); border-radius:50%; filter: brightness(.9) }
  100%{ transform: translateY(70vh) rotate(420deg) scale(.22); border-radius:50%; opacity:0 }
}
.sn-crumple *{ opacity:0; transition: opacity .15s; }

/* the dock */
.sn-dock-in{ animation: sn-dock-in .55s cubic-bezier(.34,1.56,.64,1) .15s both; }
.sn-dock-out{ animation: sn-dock-out .35s ease-in both; }
@keyframes sn-dock-in{ from{ transform: translate(-50%, 120%) scale(.8); opacity:0 } to{ transform: translate(-50%,0) scale(1); opacity:1 } }
@keyframes sn-dock-out{ to{ transform: translate(-50%, 140%) scale(.8); opacity:0 } }
.sn-plus{ position:relative; overflow:hidden; }
.sn-plus::after{ content:""; position:absolute; inset:0; background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,.55) 50%, transparent 70%); transform: translateX(-120%); animation: sn-shine 2.8s ease-in-out 1s infinite; }
@keyframes sn-shine{ 0%,60%{ transform: translateX(-120%) } 100%{ transform: translateX(120%) } }

@media (prefers-reduced-motion: reduce){
  .sn-deal,.sn-return,.sn-peel,.sn-land,.sn-crumple,.sn-dock-in,.sn-dock-out,.sn-pin-drop{ animation-duration: .01ms !important; }
}
`;

/** the little paper that peeks out of the menu icon (lives with the sidebar styles) */
export const SN_MENU_CSS = `
.sn-menu-peek{ position:absolute; right:-3px; top:-3px; width:7px; height:7px; border-radius:2px; background:#fcd34d; transform: rotate(12deg); opacity:.0; }
.group:hover .sn-menu-peek{ animation: sn-peek .6s cubic-bezier(.34,1.56,.64,1) both; }
@keyframes sn-peek{ from{ opacity:0; transform: translate(-4px,4px) rotate(0) scale(.4) } to{ opacity:1; transform: translate(0,0) rotate(14deg) scale(1) } }
`;
